import { palette } from './palette';
import { borderWidth, radius, size, space, typography } from './tokens';

/**
 * The tokens as CSS custom properties, for stylesheets (`.css` / `.scss`) that
 * cannot import `tokens.js`.
 *
 *   palette.tw.gray[500]        -> --tk-tw-gray-500
 *   palette.alpha.black[8]      -> --tk-alpha-black-8
 *   palette.alpha.x[2.5]        -> --tk-alpha-x-2_5
 *   typography.fontSize[12]     -> --tk-font-size-12
 *   typography.fontWeight.bold  -> --tk-font-weight-bold
 *   space[16] / size[120]       -> --tk-space-16: 16px / --tk-size-120: 120px
 *   radius.md / borderWidth.thin -> --tk-radius-md: 8px / --tk-border-thin: 1px
 *
 * Written onto <html> (not a portal root) because MUI and AntD portal their
 * popovers into `document.body`, outside any app root.
 */
const kebab = (s) => String(s).replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/\./g, '_').toLowerCase();

const flatten = (obj, prefix, out) => {
  Object.entries(obj).forEach(([k, v]) => {
    const name = `${prefix}-${kebab(k)}`;
    if (v && typeof v === 'object') flatten(v, name, out);
    else out[name] = String(v);
  });
  return out;
};

// A bare number means px in JS; a CSS custom property needs the unit.
const withPx = (scale) =>
  Object.fromEntries(Object.entries(scale).map(([k, v]) => [k, typeof v === 'number' && v !== 0 ? `${v}px` : v]));

export const tokenCssVars = () => ({
  ...flatten(palette, '--tk', {}),
  ...flatten(typography.fontSize, '--tk-font-size', {}),
  ...flatten(typography.fontWeight, '--tk-font-weight', {}),
  ...flatten(withPx(space), '--tk-space', {}),
  ...flatten(withPx(size), '--tk-size', {}),
  ...flatten(withPx(radius), '--tk-radius', {}),
  ...flatten(borderWidth, '--tk-border', {}),
});

export const applyTokenCssVars = (root = typeof document !== 'undefined' ? document.documentElement : null) => {
  if (!root) return;
  Object.entries(tokenCssVars()).forEach(([name, value]) => root.style.setProperty(name, value));
};

export default applyTokenCssVars;
